<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import {
  adminListAudit,
  adminListAuditActions,
  adminListEngineers,
  type AuditEntry,
  type Engineer,
} from '../api';

const entries = ref<AuditEntry[]>([]);
const actions = ref<string[]>([]);
const engineers = ref<Engineer[]>([]);

const loading = ref(false);
const error = ref<string | null>(null);

// Фильтры
const filterNumber = ref<string>('');
const filterActor = ref<string>('');
const filterAction = ref<string>('');
const filterDateFrom = ref<string>('');
const filterDateTo = ref<string>('');

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const filters: Record<string, string | number> = {};
    if (filterNumber.value) filters.number = parseInt(filterNumber.value, 10);
    if (filterActor.value) filters.actor = filterActor.value;
    if (filterAction.value) filters.action = filterAction.value;
    if (filterDateFrom.value) filters.date_from = filterDateFrom.value;
    if (filterDateTo.value) filters.date_to = filterDateTo.value;
    entries.value = await adminListAudit(filters);
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  try {
    [actions.value, engineers.value] = await Promise.all([
      adminListAuditActions(),
      adminListEngineers(),
    ]);
  } catch (e) {
    console.warn('audit load metadata failed', e);
  }
  await load();
});

// Авто-перезагрузка при смене любого фильтра (с debounce 300мс)
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(
  [filterNumber, filterActor, filterAction, filterDateFrom, filterDateTo],
  () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(load, 300);
  },
);

function actorLabel(id: string): string {
  const e = engineers.value.find((x) => x.max_user_id === id);
  return e?.full_name_override || id;
}

function actionEmoji(action: string): string {
  if (action === 'take_number') return '🆕';
  if (action === 'take_number_from_queue') return '🔄';
  if (action === 'set_client_name') return '✏️';
  if (action === 'attach_scan') return '📎';
  if (action === 'set_beneficiary') return '👥';
  if (action === 'set_b24_entered' || action.includes('b24')) return '📋';
  if (action.startsWith('admin_')) return '⚙️';
  if (action === 'replace_scan') return '🔁';
  if (action === 'cancel') return '❌';
  return '•';
}

// 2026-05-27: переводы технических action-кодов на русский для UI.
const ACTION_LABELS: Record<string, string> = {
  take_number: 'Выдан номер',
  take_number_from_queue: 'Выдан номер (из очереди)',
  set_client_name: 'Указано ФИО клиента',
  attach_scan: 'Прикреплён скан',
  attach_scan_manual: 'Прикреплён скан (вручную)',
  replace_scan: 'Заменён скан',
  set_beneficiary: 'Назначен ответственный',
  set_b24_entered: 'Помечено как внесено в Б24',
  set_b24_entered_manual: 'Помечено в Б24 (вручную)',
  cancel: 'Отменён номер',
  admin_create_contract_manual: 'Контракт создан вручную',
  admin_edit_client: 'Правка ФИО клиента',
  admin_edit_engineer: 'Смена исполнителя',
  admin_edit_engineer_name: 'Смена имени исполнителя',
  admin_edit_district: 'Смена района',
  admin_edit_date: 'Смена даты',
  admin_reset_session: 'Сброс сессии',
  admin_force_close: 'Принудительное закрытие',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] || action;
}

// Переводы ключей payload — то что показывается в "k=v · k=v ·".
const PAYLOAD_KEYS: Record<string, string> = {
  engineer_name: 'исполнитель',
  client_name: 'клиент',
  district: 'район',
  new_status: 'новый статус',
  scan_url: 'путь к скану',
  ocr_verified: 'OCR',
  old_client: 'было',
  new_client: 'стало',
  old_date: 'было',
  new_date: 'стало',
  promoted_to_assigned: 'завершён',
  pdf_renamed: 'переименован PDF',
  deal_id: 'сделка Б24',
  beneficiary_engineer_id: 'ответственный',
  beneficiary_engineer_name: 'ответственный (имя)',
  from_queue: 'из очереди',
  reason: 'причина',
  source: 'источник',
};

const PAYLOAD_VALUES: Record<string, string> = {
  claimed: 'в работе',
  assigned: 'завершён',
  confirmed: 'подтверждён',
  unconfirmed: 'не виден',
  'True': 'да',
  'False': 'нет',
  'true': 'да',
  'false': 'нет',
};

function shortPayload(p: Record<string, unknown>): string {
  // Компактное представление payload — только ключевые поля.
  const out: string[] = [];
  for (const [k, v] of Object.entries(p)) {
    if (v === null || v === undefined || v === '') continue;
    const sv = typeof v === 'object' ? JSON.stringify(v) : String(v);
    const kRu = PAYLOAD_KEYS[k] || k;
    const vRu = PAYLOAD_VALUES[sv] || sv;
    out.push(`${kRu}: ${vRu.slice(0, 40)}`);
  }
  return out.slice(0, 4).join(' · ');
}

function ts(iso: string): string {
  // 2026-05-16T08:26:11.394751 → 16.05 08:26
  if (!iso) return '';
  const m = iso.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]} ${m[4]}:${m[5]}`;
}
</script>

<template>
  <section class="flex flex-col gap-3 p-2 admin-tab-content">
    <header>
      <h2 class="text-lg font-semibold">Аудит</h2>
    </header>

    <!-- Filters -->
    <div class="grid grid-cols-2 gap-2">
      <input
        v-model="filterNumber"
        type="number"
        inputmode="numeric"
        placeholder="Номер договора"
        class="input"
      />
      <select v-model="filterAction" class="input">
        <option value="">Все действия</option>
        <option v-for="a in actions" :key="a" :value="a">{{ actionLabel(a) }}</option>
      </select>
      <select v-model="filterActor" class="input col-span-2">
        <option value="">Все пользователи</option>
        <option v-for="e in engineers" :key="e.max_user_id" :value="e.max_user_id">
          {{ e.full_name_override || e.max_user_id }} ({{ e.role }})
        </option>
      </select>
      <input v-model="filterDateFrom" type="date" class="input" />
      <input v-model="filterDateTo" type="date" class="input" />
    </div>

    <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
    <p v-if="loading" class="text-hint text-sm">Загрузка…</p>

    <ul class="flex flex-col gap-1.5">
      <li
        v-for="e in entries"
        :key="e.id"
        class="border rounded p-2 text-xs flex flex-col gap-0.5"
      >
        <div class="flex justify-between items-baseline">
          <span class="font-mono">
            {{ actionEmoji(e.action) }} {{ actionLabel(e.action) }}
            <span v-if="e.target_number">— № {{ e.target_number }}</span>
          </span>
          <span class="text-hint">{{ ts(e.created_at) }}</span>
        </div>
        <div class="text-hint">
          {{ actorLabel(e.actor_user_id) }} ({{ e.actor_role }})
        </div>
        <div v-if="Object.keys(e.payload).length" class="font-mono text-xs text-gray-600 break-all">
          {{ shortPayload(e.payload) }}
        </div>
      </li>
      <li v-if="!loading && entries.length === 0" class="text-hint text-sm text-center">
        Нет событий по фильтру
      </li>
    </ul>
  </section>
</template>
